import { useCallback, useContext, useEffect, useRef, useState } from "react";
import LocationMapInput from "../components/LocationMapInput";
import LocationForm from "../components/LocationForm";
import { locationIssue, validCoordinates } from "../utils/locationValidation";
import { directorySearchResults } from "../utils/directoryOptions";
import {
  AutoComplete,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { PlusOutlined, SearchOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../context/AuthContext";
import { useGlobalToast } from "../context/ToastProvider";
import {
  archiveDirectoryLocation,
  archiveDirectoryPartner,
  listDirectoryLocations,
  listDirectoryLocationsPage,
  listDirectoryPartners,
  listDirectoryPartnersPage,
  saveDirectoryLocation,
  saveDirectoryPartner,
  searchSharedDirectory,
  setDirectoryPartnerFavorite,
  setDirectoryLocationFavorite,
} from "../services/fleetDirectoryService";

const PARTNER_TABS = new Set(["CUSTOMER", "CONSIGNOR", "CONSIGNEE"]);
const ROLE_COLORS = { CUSTOMER: "blue", CONSIGNOR: "gold", CONSIGNEE: "green", PICKUP: "purple", DROPOFF: "cyan" };

function emptyPartnerLocationDraft(role = "PICKUP") {
  return {
    role,
    name: "",
    streetName: "",
    cityName: "",
    countryCode: "",
    latitude: null,
    longitude: null,
  };
}

export default function Directory() {
  const { t } = useTranslation();
  const { getFleetID } = useContext(AuthContext);
  const toast = useGlobalToast();
  const fleetId = getFleetID();
  const [activeTab, setActiveTab] = useState("CUSTOMER");
  const [partners, setPartners] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [tableTotal, setTableTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedSearchResult, setSelectedSearchResult] = useState(null);
  const searchRequest = useRef(0);
  const tableRequest = useRef(0);
  const [locationScope, setLocationScope] = useState("MINE");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modalType, setModalType] = useState("");
  const [locationSaveAttempted, setLocationSaveAttempted] = useState(false);
  const [sharedSuggestions, setSharedSuggestions] = useState([]);
  const [sharedOrganizationLocations, setSharedOrganizationLocations] = useState([]);
  const [partnerForm] = Form.useForm();
  const [locationForm] = Form.useForm();
  const [partnerLocationDrafts, setPartnerLocationDrafts] = useState([emptyPartnerLocationDraft()]);
  const watchedPartnerRoles = Form.useWatch("roles", partnerForm) || [];
  const watchedOrderFinalizedEmailsEnabled = Form.useWatch("orderFinalizedEmailsEnabled", partnerForm) === true;
  const watchedOrderActivatedEmailsEnabled = Form.useWatch("orderActivatedEmailsEnabled", partnerForm) === true;
  const watchedPickupIds = Form.useWatch("pickupLocationIds", partnerForm) || [];
  const watchedDropoffIds = Form.useWatch("dropoffLocationIds", partnerForm) || [];

  const loadTable = useCallback(async () => {
    if (!fleetId) return;
    const request = ++tableRequest.current;
    setLoading(true);
    try {
      const params = { role: activeTab, query: debouncedQuery, page, pageSize, scope: locationScope };
      const result = PARTNER_TABS.has(activeTab)
        ? await listDirectoryPartnersPage(fleetId, params)
        : await listDirectoryLocationsPage(fleetId, { ...params, scope: locationScope });
      if (request !== tableRequest.current) return;
      setTableRows(result.items);
      setTableTotal(Number(result.pagination?.total) || 0);
      if (result.pagination?.page && Number(result.pagination.page) !== page) setPage(Number(result.pagination.page));
    } catch (error) {
      if (request !== tableRequest.current) return;
      toast({ error, title: t("feedback.directoryLoad"), status: "error" });
    } finally {
      if (request === tableRequest.current) setLoading(false);
    }
  }, [activeTab, debouncedQuery, fleetId, locationScope, page, pageSize, t, toast]);

  const load = useCallback(async () => {
    if (!fleetId) return;
    setSelectedSearchResult(null);
    setLoading(true);
    try {
      const [nextPartners, nextLocations] = await Promise.all([
        listDirectoryPartners(fleetId),
        listDirectoryLocations(fleetId),
      ]);
      setPartners(nextPartners);
      setLocations(nextLocations);
    } catch (error) {
      toast({ error, title: t("feedback.directoryLoad"), status: "error" });
    } finally {
      setLoading(false);
    }
  }, [fleetId, t, toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  useEffect(() => {
    const request = ++searchRequest.current;
    if (!fleetId || !debouncedQuery) { setSearchResults([]); return; }
    let cancelled = false;
    void Promise.all([
      listDirectoryPartners(fleetId, { query: debouncedQuery, scope: "ALL" }),
      listDirectoryLocations(fleetId, { query: debouncedQuery, scope: "ALL" }),
    ]).then(([companies, places]) => {
      if (cancelled || request !== searchRequest.current) return;
      const results = directorySearchResults(companies, places, activeTab);
      setSearchResults(results);
    }).catch(error => {
      if (!cancelled && request === searchRequest.current) {
        setSearchResults([]);
        toast({ error, title: t("feedback.directorySearch"), status: "error" });
      }
    });
    return () => { cancelled = true; };
  }, [fleetId, debouncedQuery, activeTab, t, toast]);

  const selectSearchResult = (key) => {
    const result = searchResults.find(item => item.key === key);
    if (!result) return;
    setSelectedSearchResult(result);
    setSearchQuery(result.record.name);
    setActiveTab(result.role);
    setLocationScope("ALL");
    setPage(1);
  };

  useEffect(() => {
    loadTable();
  }, [loadTable]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, debouncedQuery, locationScope]);

  const openPartner = (record = null) => {
    const role = PARTNER_TABS.has(activeTab) ? activeTab : "CUSTOMER";
    setEditing(record?.directorySource === "GLOBAL" ? { ...record, id: undefined } : record);
    setModalType("PARTNER");
    setSharedSuggestions([]);
    setSharedOrganizationLocations([]);
    setPartnerLocationDrafts([emptyPartnerLocationDraft()]);
    partnerForm.setFieldsValue({
      name: record?.name || "",
      legalName: record?.legalName || "",
      roles: record?.roles || [role],
      taxId: record?.taxId || "",
      email: record?.email || "",
      phone: record?.phone || "",
      website: record?.website || "",
      streetName: record?.address?.streetName || "",
      postalCode: record?.address?.postalCode || "",
      cityName: record?.address?.cityName || "",
      countryCode: record?.address?.countryCode || "",
      pickupLocationIds: record?.pickupLocationIds || [],
      dropoffLocationIds: record?.dropoffLocationIds || [],
      orderFinalizedEmailsEnabled: record?.orderFinalizedEmailsEnabled === true,
      orderActivatedEmailsEnabled: record?.orderActivatedEmailsEnabled === true,
      globalOrganizationId: record?.globalOrganizationId || "",
      sharePublicDetails: record ? false : true,
    });
  };

  const openLocation = (record = null) => {
    setLocationSaveAttempted(false);
    const role = PARTNER_TABS.has(activeTab) ? "PICKUP" : activeTab;
    setEditing(record);
    setModalType("LOCATION");
    setSharedSuggestions([]);
    locationForm.setFieldsValue({
      category: record?.category,
      placeTypes: record?.placeTypes || ["DELIVERY_POINT"],
      name: record?.name || "",
      roles: record?.roles || [role],
      streetName: record?.address?.streetName || "",
      postalCode: record?.address?.postalCode || "",
      cityName: record?.address?.cityName || "",
      countryCode: record?.address?.countryCode || "",
      latitude: record?.latitude,
      longitude: record?.longitude,
      instructions: record?.instructions || "",
      globalLocationId: record?.globalLocationId || "",
      sharePublicDetails: record ? false : true,
    });
  };

  const searchShared = async (query, type) => {
    if (!fleetId || String(query || "").trim().length < 2) {
      setSharedSuggestions([]);
      return;
    }
    try {
      setSharedSuggestions(await searchSharedDirectory(fleetId, query, type));
    } catch {
      setSharedSuggestions([]);
    }
  };

  const selectSharedPartner = (id) => {
    const suggestion = sharedSuggestions.find((item) => item.id === id);
    if (!suggestion) return;
    partnerForm.setFieldsValue({
      name: suggestion.name,
      legalName: suggestion.legalName || suggestion.name,
      taxId: suggestion.taxId || "",
      phone: suggestion.phone || "",
      website: suggestion.website || "",
      streetName: suggestion.address?.streetName || "",
      postalCode: suggestion.address?.postalCode || "",
      cityName: suggestion.address?.cityName || "",
      countryCode: suggestion.address?.countryCode || "",
      globalOrganizationId: suggestion.id,
    });
    setSharedOrganizationLocations(suggestion.publicLocations || []);
  };

  const adoptSharedLocation = async (location) => {
    try {
      const saved = await saveDirectoryLocation(fleetId, {
        ...location,
        globalLocationId: location.id || "",
      });
      const role = location.roles?.includes("DROPOFF") && !location.roles?.includes("PICKUP")
        ? "DROPOFF"
        : "PICKUP";
      const field = role === "PICKUP" ? "pickupLocationIds" : "dropoffLocationIds";
      partnerForm.setFieldValue(field, Array.from(new Set([...(partnerForm.getFieldValue(field) || []), saved.id])));
      setLocations((previous) => [...previous.filter((item) => item.id !== saved.id), saved]);
      setSharedOrganizationLocations((previous) => previous.filter((item) => item !== location));
    } catch (error) {
      toast({ error, title: t("feedback.directorySave"), status: "error" });
    }
  };

  const savePartner = async () => {
    try {
      const values = await partnerForm.validateFields();
      let pickupLocationIds = values.pickupLocationIds || [];
      let dropoffLocationIds = values.dropoffLocationIds || [];
      if (
        pickupLocationIds.length + dropoffLocationIds.length === 0
      ) {
        for (const draft of partnerLocationDrafts.filter((item) => String(item.name || "").trim())) {
          const savedLocation = await saveDirectoryLocation(fleetId, {
            name: draft.name,
            roles: [draft.role],
            address: {
              streetName: draft.streetName,
              cityName: draft.cityName,
              postalCode: draft.postalCode || "",
              countryCode: draft.countryCode,
            },
            latitude: draft.latitude,
            longitude: draft.longitude,
            sharePublicDetails: values.sharePublicDetails,
          });
          if (draft.role === "PICKUP") pickupLocationIds.push(savedLocation.id);
          else dropoffLocationIds.push(savedLocation.id);
        }
      }
      await saveDirectoryPartner(fleetId, {
        ...(editing || {}),
        ...values,
        address: {
          streetName: values.streetName,
          postalCode: values.postalCode,
          cityName: values.cityName,
          countryCode: values.countryCode,
        },
        pickupLocationIds,
        dropoffLocationIds,
      });
      setModalType("");
      await Promise.all([load(), loadTable()]);
      toast({ title: t("directory.saved"), status: "success" });
    } catch (error) {
      if (error?.errorFields) return;
      toast({ error, title: t("feedback.directorySave"), status: "error" });
    }
  };

  const saveLocation = async () => {
    setLocationSaveAttempted(true);
    try {
      const values = locationForm.getFieldsValue(true);
      const issue = locationIssue(values);
      if (issue) { toast({ title: t("validationReviewFields"), status: "warning" }); return; }
      await saveDirectoryLocation(fleetId, {
        ...(editing || {}),
        ...values,
        address: {
          streetName: values.streetName,
          postalCode: values.postalCode,
          cityName: values.cityName,
          countryCode: values.countryCode,
        },
      });
      setModalType("");
      await Promise.all([load(), loadTable()]);
      toast({ title: t("directory.saved"), status: "success" });
    } catch (error) {
      if (error?.errorFields) return;
      toast({ error, title: t("feedback.directorySave"), status: "error" });
    }
  };

  const columns = PARTNER_TABS.has(activeTab)
    ? [
        { title: t("directory.name"), dataIndex: "name", key: "name" },
        { title: t("directory.email"), dataIndex: "email", key: "email", render: (value) => value || "—" },
        {
          title: t("directory.roles"),
          dataIndex: "roles",
          key: "roles",
          render: (roles) => roles?.map((role) => <Tag key={role} color={ROLE_COLORS[role]}>{t(`directory.role.${role}`)}</Tag>),
        },
        {
          title: t("directory.locations"),
          key: "locations",
          render: (_, record) => (record.pickupLocationIds?.length || 0) + (record.dropoffLocationIds?.length || 0),
        },
        {
          title: t("directory.source"),
          key: "source",
          render: (_, record) => <Tag color={record.directorySource === "GLOBAL" ? "purple" : "blue"}>{t(record.directorySource === "GLOBAL" ? "directory.sourceGlobal" : "directory.sourceFleet")}</Tag>,
        },
        {
          title: t("directory.actions"),
          key: "actions",
          render: (_, record) => (
            <Space>
              <Button type="text" title={t(record.favorite ? "directory.removeFavorite" : "directory.addFavorite")}
                icon={record.favorite ? <StarFilled style={{ color: "#faad14" }} /> : <StarOutlined />}
                onClick={async () => {
                  try { await setDirectoryPartnerFavorite(fleetId, record.id, !record.favorite); await loadTable(); }
                  catch (error) { toast({ error, title: t("feedback.favorite"), status: "error" }); }
                }} />
              <Button size="small" onClick={() => openPartner(record)}>{t("directory.edit")}</Button>
              {record.directorySource !== "GLOBAL" && <Popconfirm title={t("directory.archiveConfirm")} onConfirm={async () => {
                try {
                  await archiveDirectoryPartner(fleetId, record.id);
                  await load();
                  await loadTable();
                } catch (error) {
                  toast({ error, title: t("feedback.directoryArchive"), status: "error" });
                }
              }}>
                <Button danger size="small">{t("directory.archive")}</Button>
              </Popconfirm>}
            </Space>
          ),
        },
      ]
    : [
        { title: t("directory.name"), dataIndex: "name", key: "name" },
        { title: t("directory.address"), key: "address", render: (_, record) => <div>
          {[record.address?.streetName, record.address?.cityName].filter(Boolean).join(", ") || "—"}
          {!validCoordinates(record.latitude, record.longitude) && <div><Tag color="error">{t("placeForm.coordinates")}</Tag></div>}
          {!record.address?.streetName && <div><Tag color="error">{t("placeForm.address")}</Tag></div>}
        </div> },
        {
          title: t("directory.roles"),
          dataIndex: "roles",
          key: "roles",
          render: (roles) => roles?.map((role) => <Tag key={role} color={ROLE_COLORS[role]}>{t(`directory.role.${role}`)}</Tag>),
        },
        {
          title: t("directory.source"),
          key: "source",
          render: (_, record) => (
            <Space size={4} wrap>
              <Tag color={record.directorySource === "GLOBAL" ? "purple" : "blue"}>
                {record.directorySource === "GLOBAL" ? t("directory.sourceGlobal") : t("directory.sourceFleet")}
              </Tag>
              {record.correctionPending && <Tag color="gold">{t("directory.pendingReview")}</Tag>}
            </Space>
          ),
        },
        {
          title: t("directory.actions"),
          key: "actions",
          render: (_, record) => (
            <Space>
              {record.directorySource === "GLOBAL" && (
                <Button
                  type="text"
                  aria-label={record.favorite ? t("directory.removeFavorite") : t("directory.addFavorite")}
                  title={record.favorite ? t("directory.removeFavorite") : t("directory.addFavorite")}
                  icon={record.favorite ? <StarFilled style={{ color: "#faad14" }} /> : <StarOutlined />}
                  onClick={async () => {
                    try {
                      await setDirectoryLocationFavorite(fleetId, record.id, !record.favorite);
                      await Promise.all([load(), loadTable()]);
                    } catch (error) {
                      toast({ error, title: t("feedback.favorite"), status: "error" });
                    }
                  }}
                />
              )}
              <Button size="small" onClick={() => openLocation(record)}>{t("directory.edit")}</Button>
              <Popconfirm title={t("directory.archiveConfirm")} onConfirm={async () => {
                try {
                  await archiveDirectoryLocation(fleetId, record.id);
                  await load();
                  await loadTable();
                } catch (error) {
                  toast({ error, title: t("feedback.directoryArchive"), status: "error" });
                }
              }}>
                <Button danger size="small">{t("directory.archive")}</Button>
              </Popconfirm>
            </Space>
          ),
        },
      ];

  const locationOptions = locations.map((item) => ({
    value: item.id,
    label: `${item.name}${item.address?.cityName ? ` — ${item.address.cityName}` : ""}`,
  }));

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <Typography.Title level={2} style={{ margin: 0 }}>{t("directory.title")}</Typography.Title>
          <Typography.Text type="secondary">{t("directory.subtitle")}</Typography.Text>
        </div>
        <Space wrap>
        <AutoComplete value={searchQuery} allowClear style={{ width: 320, maxWidth: "100%" }}
          onSearch={(value) => { ++searchRequest.current; setSearchResults([]); setSelectedSearchResult(null); setSearchQuery(value); setPage(1); }}
          onClear={() => { setSearchQuery(""); setSelectedSearchResult(null); setSearchResults([]); }}
          onSelect={selectSearchResult}
          options={searchResults.map(result => ({ value: result.key, label: (
            <Space wrap><span>{result.record.name}</span>{result.roles.map(role => <Tag key={role}>{t(`directory.role.${role}`)}</Tag>)}
              <Tag>{t(result.record.directorySource === "GLOBAL" ? "directory.sourceGlobal" : "directory.sourceFleet")}</Tag>
            </Space>
          ) }))}>
          <Input prefix={<SearchOutlined />} placeholder={t("directory.searchPlaceholder")} />
        </AutoComplete>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => PARTNER_TABS.has(activeTab) ? openPartner() : openLocation()}>
          {PARTNER_TABS.has(activeTab) ? t("directory.addPartner") : t("directory.addLocation")}
        </Button>
        </Space>
      </div>
      <Tabs activeKey={activeTab} onChange={(value) => { setSelectedSearchResult(null); setActiveTab(value); }} items={[
        { key: "CUSTOMER", label: t("directory.tab.customers") },
        { key: "CONSIGNOR", label: t("directory.tab.consignors") },
        { key: "CONSIGNEE", label: t("directory.tab.consignees") },
        { key: "PICKUP", label: t("directory.tab.pickups") },
        { key: "DROPOFF", label: t("directory.tab.dropoffs") },
      ]} />
      <div style={{ display: "flex", gap: 12, justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
          <Radio.Group
            value={locationScope}
            onChange={(event) => { setSelectedSearchResult(null); setLocationScope(event.target.value); }}
            optionType="button"
            buttonStyle="solid"
            options={[
              { value: "MINE", label: t("directory.scopeMine") },
              { value: "FAVORITES", label: t("directory.scopeFavorites") },
              { value: "GLOBAL", label: t("directory.scopeGlobal") },
              { value: "ALL", label: t("directory.scopeAll") },
            ]}
          />
      </div>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={selectedSearchResult ? [tableRows.find(item => item.id === selectedSearchResult.record.id) || selectedSearchResult.record] : tableRows}
        pagination={{
          current: page,
          pageSize,
          total: selectedSearchResult ? 1 : tableTotal,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total) => t("directory.totalContacts", { total }),
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPageSize === pageSize ? nextPage : 1);
            setPageSize(nextPageSize);
          },
        }}
      />

      <Modal title={editing ? t("directory.editPartner") : t("directory.addPartner")} open={modalType === "PARTNER"} onCancel={() => setModalType("")} onOk={savePartner} width={760}>
        <Form form={partnerForm} layout="vertical">
          {!editing && (
            <Form.Item label={t("directory.sharedSuggestion")}>
              <Select showSearch filterOption={false} allowClear onSearch={(value) => searchShared(value, "ORGANIZATION")} onChange={selectSharedPartner}
                options={sharedSuggestions.map((item) => ({ value: item.id, label: `${item.name}${item.address?.cityName ? ` — ${item.address.cityName}` : ""}` }))} />
            </Form.Item>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Form.Item name="name" label={t("directory.name")} rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="legalName" label={t("directory.legalName")}><Input /></Form.Item>
          </div>
          <Form.Item name="roles" label={t("directory.roles")} rules={[{ required: true }]}>
            <Checkbox.Group options={["CUSTOMER", "CONSIGNOR", "CONSIGNEE"].map((value) => ({ value, label: t(`directory.role.${value}`) }))} />
          </Form.Item>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <Form.Item name="taxId" label={t("directory.taxId")}><Input /></Form.Item>
            <Form.Item
              name="email"
              label={t("directory.email")}
              rules={[{
                required: watchedPartnerRoles.includes("CUSTOMER") && (watchedOrderFinalizedEmailsEnabled || watchedOrderActivatedEmailsEnabled),
              }, { type: "email" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="phone" label={t("directory.phone")}><Input /></Form.Item>
          </div>
          {watchedPartnerRoles.includes("CUSTOMER") && (
            <Form.Item name="orderActivatedEmailsEnabled" valuePropName="checked">
              <Checkbox>{t("directory.orderActivatedEmailsEnabled")}</Checkbox>
            </Form.Item>
          )}
          {watchedPartnerRoles.includes("CUSTOMER") && (
            <Form.Item name="orderFinalizedEmailsEnabled" valuePropName="checked">
              <Checkbox>{t("directory.orderFinalizedEmailsEnabled")}</Checkbox>
            </Form.Item>
          )}
          <Form.Item name="website" label={t("directory.website")}><Input /></Form.Item>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 100px", gap: 12 }}>
            <Form.Item name="streetName" label={t("directory.address")}><Input /></Form.Item>
            <Form.Item name="postalCode" label={t("directory.postalCode")}><Input /></Form.Item>
            <Form.Item name="cityName" label={t("directory.city")}><Input /></Form.Item>
            <Form.Item name="countryCode" label={t("directory.country")}><Input maxLength={3} /></Form.Item>
          </div>
          <Form.Item name="pickupLocationIds" label={t("directory.pickupLocations")}><Select mode="multiple" options={locationOptions.filter((option) => locations.find((item) => item.id === option.value)?.roles?.includes("PICKUP"))} /></Form.Item>
          <Form.Item name="dropoffLocationIds" label={t("directory.dropoffLocations")}><Select mode="multiple" options={locationOptions.filter((option) => locations.find((item) => item.id === option.value)?.roles?.includes("DROPOFF"))} /></Form.Item>
          {watchedPickupIds.length + watchedDropoffIds.length === 0 && (
              <div style={{ display: "grid", gap: 8, border: "1px solid #e5e9f2", borderRadius: 8, padding: 10, marginBottom: 16 }}>
                <Typography.Text strong>{t("directory.createOptionalLocation")}</Typography.Text>
                {partnerLocationDrafts.map((draft, index) => {
                  const updateDraft = (patch) => setPartnerLocationDrafts((previous) =>
                    previous.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item)
                  );
                  return (
                    <div key={index} style={{ display: "grid", gap: 8, borderTop: index ? "1px solid #e5e9f2" : "none", paddingTop: index ? 10 : 0 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}>
                        <Select
                          value={draft.role}
                          onChange={(value) => updateDraft({ role: value })}
                          options={[
                            { value: "PICKUP", label: t("directory.role.PICKUP") },
                            { value: "DROPOFF", label: t("directory.role.DROPOFF") },
                          ]}
                        />
                        {partnerLocationDrafts.length > 1 && (
                          <Button danger onClick={() => setPartnerLocationDrafts((previous) => previous.filter((_, itemIndex) => itemIndex !== index))}>−</Button>
                        )}
                      </div>
                      <Input value={draft.name} placeholder={`${t("directory.name")} *`} onChange={(event) => updateDraft({ name: event.target.value })} />
                      <LocationMapInput value={draft} onChange={updateDraft} />
                    </div>
                  );
                })}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => setPartnerLocationDrafts((previous) => [...previous, emptyPartnerLocationDraft("DROPOFF")])}>
                  {t("directory.addLocation")}
                </Button>
              </div>
            )}
          {sharedOrganizationLocations.length > 0 && (
            <Form.Item label={t("directory.knownLocations")}>
              <Space wrap>
                {sharedOrganizationLocations.map((location, index) => (
                  <Button key={`${location.name}-${index}`} size="small" onClick={() => adoptSharedLocation(location)}>
                    {t("directory.adoptLocation")}: {location.name}
                  </Button>
                ))}
              </Space>
            </Form.Item>
          )}
          {!editing && <Form.Item name="sharePublicDetails" valuePropName="checked"><Checkbox>{t("directory.sharePublicDetails")}</Checkbox></Form.Item>}
          <Form.Item name="globalOrganizationId" hidden><Input /></Form.Item>
        </Form>
      </Modal>

      <Modal destroyOnClose title={editing ? t("directory.editLocation") : t("directory.addLocation")} open={modalType === "LOCATION"} onCancel={() => setModalType("")} onOk={saveLocation} width={720}>
        <Form form={locationForm} layout="vertical">
          <Form.Item shouldUpdate>{() => <LocationForm fleetId={fleetId} editing={Boolean(editing)}
            showErrors={locationSaveAttempted}
            value={locationForm.getFieldsValue(true)} onChange={next => locationForm.setFieldsValue(next)} />}</Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
